import React from "react";
import {
  Paper,
  Typography,
  FormControl,
  TextField,
  Button,
  Grid,
  Autocomplete,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { selectCurrentagency } from "../../../redux/features/authSlice";
import { toast } from "react-toastify";
import {
  usePostExpenseMutation,
  useUpdateExpenseMutation,
} from "../../../redux/api/expenses";
import { useGetVendorsQuery } from "../../../redux/api/vendor";

const ExpenseForm = ({
  types,
  date,
  setDate,
  amount,
  setAmount,
  type,
  setType,
  vendor,
  setVendor,
  description,
  setDescription,
  mode = "add",
  editRow = null,
  onSuccess,
  onFinish,
}) => {
  const [postExpense, { isLoading: isPosting }] = usePostExpenseMutation();
  const [putExpense, { isLoading: isUpdating }] = useUpdateExpenseMutation();
  const currentAgency = useSelector(selectCurrentagency);

  const { data: vendors = [] } = useGetVendorsQuery(currentAgency.agencyid);

  const handleSubmit = async () => {
    if (!type || !amount || !date || !vendor || !description) {
      toast.error("All fields are required");
      return;
    }

    const payload = {
      agencyid: currentAgency.agencyid,
      exptype: type,
      expdate: dayjs(date).format("YYYY-MM-DD"),
      amount: Number(amount),
      vendorid: vendor,
      description, // ✅ added
    };

    try {
      if (mode === "add") {
        await postExpense(payload).unwrap();
      } else if (mode === "edit" && editRow?.id) {
        await putExpense({ id: editRow.id, ...payload }).unwrap();
      }
      onSuccess?.();
      onFinish?.();
    } catch (err) {
      console.error("Error saving expense:", err);
      toast.error("Failed to save expense");
    }
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }} elevation={3}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {mode === "add" ? "Add Expense" : "Edit Expense"}
      </Typography>

      <Grid container spacing={2}>
        {/* Date Field */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <DatePicker
              label="Date"
              value={date}
              onChange={(v) => setDate(v)}
              slotProps={{ textField: { size: "small", variant: "outlined" } }}
            />
          </FormControl>
        </Grid>

        {/* Type Field */}
        <Grid item xs={12} md={6}>
          <Autocomplete
            size="small"
            options={types}
            getOptionLabel={(option) => option.name || ""}
            value={types.find((t) => t.id === type) || null}
            onChange={(_, newValue) => setType(newValue ? newValue.id : "")}
            renderInput={(params) => (
              <TextField {...params} label="Type" variant="outlined" />
            )}
          />
        </Grid>

        {/* Vendor Field */}
        <Grid item xs={12} md={6}>
          <Autocomplete
            size="small"
            options={vendors}
            getOptionLabel={(option) => option.vendor || ""}
            value={vendors.find((v) => v.id === vendor) || null}
            onChange={(_, newValue) => setVendor(newValue ? newValue.id : "")}
            renderInput={(params) => (
              <TextField {...params} label="Vendor" variant="outlined" />
            )}
          />
        </Grid>

        {/* Amount Field */}
        <Grid item xs={12} md={6}>
          <TextField
            label="Amount"
            fullWidth
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            size="small"
            variant="outlined"
          />
        </Grid>

        {/* ✅ Description Field */}
        <Grid item xs={12}>
          <TextField
            label="Description"
            fullWidth
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            size="small"
            variant="outlined"
            multiline
            rows={3}
          />
        </Grid>

        {/* Save / Update Button */}
        <Grid item xs={12}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isPosting || isUpdating}
            sx={{ color: "white" }}
          >
            {isPosting || isUpdating
              ? "Saving..."
              : mode === "add"
              ? "Save"
              : "Update"}
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};
export default ExpenseForm;
